import React from 'react';
import { Newspaper, LineChart, Sparkles } from 'lucide-react';

// Renders the supplementary data /social/listening/search layers on top of
// the canonical search (news/market/AI synthesis) — a separate, parallel
// call whose post list is discarded in favor of /social/search's (which has
// relevance scoring + cursor pagination the listening endpoint doesn't
// thread through). See SocialIntelligence.tsx for how the two are combined.

interface EnrichmentPanelProps {
  loading: boolean;
  newsArticles?: any[];
  marketData?: any;
  aiSynthesis?: any;
}

export const EnrichmentPanel: React.FC<EnrichmentPanelProps> = ({ loading, newsArticles, marketData, aiSynthesis }) => {
  const hasNews = (newsArticles?.length ?? 0) > 0;
  const hasMarket = !!marketData;
  const hasAi = !!(aiSynthesis?.summary || aiSynthesis?.text || (typeof aiSynthesis === 'string' && aiSynthesis));

  if (!loading && !hasNews && !hasMarket && !hasAi) return null;

  const aiText = typeof aiSynthesis === 'string' ? aiSynthesis : (aiSynthesis?.summary ?? aiSynthesis?.text ?? '');

  return (
    <div className="mx-6 mt-4 rounded-lg p-4 space-y-3" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
      <div className="flex items-center gap-2">
        <Sparkles size={13} style={{ color: '#00aaff' }} />
        <p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: '#00aaff' }}>Enrichment</p>
        {loading && <span className="text-[10px] text-gray-600 font-mono">Loading news/market/AI context...</span>}
      </div>

      {hasAi && (
        <p className="text-xs text-gray-300 leading-relaxed">{aiText}</p>
      )}

      {hasMarket && (
        <div className="flex items-center gap-2 text-[11px] font-mono">
          <LineChart size={11} style={{ color: '#22c55e' }} />
          <span className="text-gray-500">Market:</span>
          <span className="text-white">{JSON.stringify(marketData).slice(0, 140)}</span>
        </div>
      )}

      {hasNews && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Newspaper size={11} style={{ color: '#a855f7' }} />
            <span className="text-[10px] text-gray-600 font-mono uppercase">Related news ({newsArticles!.length})</span>
          </div>
          <div className="space-y-1">
            {newsArticles!.slice(0, 5).map((a: any, i: number) => (
              <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                className="block text-[11px] text-gray-400 hover:text-gray-200 truncate">
                · {a.title ?? a.headline ?? 'Untitled'}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrichmentPanel;
